// src/components/RPA/buttons/SamplingButton.js
import React, { useState } from 'react';
import axios from 'axios';

const api = process.env.REACT_APP_API;

const SamplingButton = ({ docId, onDone }) => {
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);
  const [payload, setPayload] = useState(null);
  const [err, setErr] = useState('');
  
  // Sampling parameters
  const [populationSize, setPopulationSize] = useState('');
  const [confidenceLevel, setConfidenceLevel] = useState(95);
  const [tolerableError, setTolerableError] = useState(5);

  const run = async () => {
    if (!docId) return;
    
    if (!populationSize || populationSize < 1) {
      setErr('Please enter a valid population size');
      setOpen(true);
      return;
    }

    setBusy(true);
    setErr('');
    setPayload(null);

    try {
      const res = await axios.post(
        `${api}/admin/rpa/sampling/generate`,
        { 
          docId,
          populationSize: parseInt(populationSize, 10),
          confidenceLevel,
          tolerableError 
        },
        { withCredentials: true, validateStatus: () => true }
      );

      if (res.status === 200) {
        setPayload(res.data || {});
      } else {
        setErr(res.data?.message || `Unexpected status ${res.status}`);
      }

      setOpen(true);
      if (typeof onDone === 'function') onDone(); // refresh doc list
    } catch (e) {
      console.error('[RPA] sampling failed:', e);
      setErr('Sampling generation failed. Check backend logs.');
      setOpen(true);
    } finally {
      setBusy(false);
    }
  };

  const renderSamplingResult = () => {
    if (!payload) return null;

    const isFullPopulation = payload.approach === 'full_population';
    const testing = payload.testing || {};

    return (
      <div style={resultBox}>
        <div style={{ 
          padding: '8px 12px', 
          borderRadius: 6, 
          marginBottom: 12,
          background: isFullPopulation ? '#f0f9ff' : '#f0fdf4',
          border: `1px solid ${isFullPopulation ? '#0ea5e9' : '#22c55e'}`
        }}>
          <div style={{ 
            fontWeight: 'bold', 
            color: isFullPopulation ? '#0369a1' : '#15803d',
            marginBottom: 4
          }}>
            {isFullPopulation ? 'Full Population Testing' : 'Statistical Sampling'}
          </div>
          <div style={{ fontSize: 13, color: '#555' }}>
            {payload.message}
          </div>
        </div>

        <div style={{ display: 'grid', gap: 8, fontSize: 13 }}>
          <div><strong>Workflow:</strong> {payload.workflowType}</div>
          <div><strong>Population Size:</strong> {testing.populationSize?.toLocaleString()}</div>
          
          {isFullPopulation ? (
            <>
              <div><strong>Testing Coverage:</strong> {testing.coverage} (Complete)</div>
              <div><strong>Reason:</strong> {testing.reason}</div>
            </>
          ) : (
            <>
              <div><strong>Sample Size:</strong> {testing.sampleSize}</div>
              <div><strong>Coverage:</strong> {testing.coverage}</div>
              <div><strong>Confidence Level:</strong> {testing.confidenceLevel}</div>
              <div><strong>Tolerable Error:</strong> {testing.tolerableError}</div>
            </>
          )}
        </div>

        {payload.sample && (
          <div style={{ marginTop: 12 }}>
            <strong style={{ fontSize: 12 }}>Sample Items:</strong>
            <div style={{ 
              marginTop: 4, 
              maxHeight: 120, 
              overflowY: 'auto',
              background: '#f8f9fa',
              padding: 8,
              borderRadius: 4,
              fontSize: 12,
              fontFamily: 'monospace'
            }}>
              {payload.sample.indices ? payload.sample.indices.join(', ') : 'No sample indices'}
            </div>
            <div style={{ fontSize: 11, color: '#666', marginTop: 4 }}>
              Method: {payload.sample.method} • Generated: {new Date(payload.sample.generatedAt).toLocaleString()}
            </div>
          </div>
        )}

        {payload.nextSteps && (
          <div style={{ marginTop: 12 }}>
            <strong style={{ fontSize: 12 }}>Next Steps:</strong>
            <ul style={{ margin: '4px 0 0 16px', fontSize: 12 }}>
              {payload.nextSteps.map((step, idx) => (
                <li key={idx} style={{ marginTop: 2 }}>{step}</li>
              ))}
            </ul>
          </div>
        )}

        {payload.auditRisk && (
          <div style={{ marginTop: 12, padding: 8, background: '#fffbeb', borderRadius: 4, border: '1px solid #fbbf24' }}>
            <strong style={{ fontSize: 12, color: '#92400e' }}>Audit Risk Assessment:</strong>
            <div style={{ fontSize: 11, color: '#92400e', marginTop: 2 }}>
              Sampling Risk: {payload.auditRisk.samplingRisk} • Overall Assurance: {payload.auditRisk.overallAssurance}
            </div>
          </div>
        )}
      </div>
    );
  };

  if (!open) {
    return (
      <span style={{ display: 'inline-block' }}>
        <button 
          onClick={() => setOpen(true)} 
          disabled={busy}
          style={{ background: '#f3f4f6', border: '1px solid #d1d5db' }}
        >
          Generate Sampling
        </button>
      </span>
    );
  }

  return (
    <span style={{ display: 'inline-block' }}>
      <div style={panel}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
          <strong>Sampling Generation</strong>
          <button onClick={() => setOpen(false)} style={{ padding: '2px 8px' }}>
            Close
          </button>
        </div>

        {!payload && (
          <div style={{ marginBottom: 12 }}>
            <div style={{ display: 'grid', gap: 8, fontSize: 13 }}>
              <div>
                <label style={{ display: 'block', marginBottom: 4, fontWeight: 500 }}>
                  Population Size *
                </label>
                <input
                  type="number"
                  min="1"
                  value={populationSize}
                  onChange={(e) => setPopulationSize(e.target.value)}
                  placeholder="e.g., 1000"
                  style={{ width: '100%', padding: '4px 8px', border: '1px solid #d1d5db', borderRadius: 4 }}
                  required
                />
              </div>
              
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                <div>
                  <label style={{ display: 'block', marginBottom: 4, fontWeight: 500 }}>
                    Confidence %
                  </label>
                  <select
                    value={confidenceLevel}
                    onChange={(e) => setConfidenceLevel(parseInt(e.target.value))}
                    style={{ width: '100%', padding: '4px 8px', border: '1px solid #d1d5db', borderRadius: 4 }}
                  >
                    <option value={90}>90%</option>
                    <option value={95}>95%</option>
                    <option value={99}>99%</option>
                  </select>
                </div>
                
                <div>
                  <label style={{ display: 'block', marginBottom: 4, fontWeight: 500 }}>
                    Tolerable Error %
                  </label>
                  <input
                    type="number"
                    min="0.1"
                    max="20"
                    step="0.1"
                    value={tolerableError}
                    onChange={(e) => setTolerableError(parseFloat(e.target.value))}
                    style={{ width: '100%', padding: '4px 8px', border: '1px solid #d1d5db', borderRadius: 4 }}
                  />
                </div>
              </div>
            </div>
            
            <div style={{ marginTop: 12, display: 'flex', gap: 8 }}>
              <button 
                onClick={run} 
                disabled={busy || !populationSize}
                style={{ 
                  background: '#3b82f6', 
                  color: 'white', 
                  border: 'none', 
                  padding: '6px 12px', 
                  borderRadius: 4,
                  cursor: populationSize ? 'pointer' : 'not-allowed'
                }}
              >
                {busy ? 'Generating…' : 'Generate'}
              </button>
            </div>
          </div>
        )}

        {err && <p style={{ color: 'crimson', marginBottom: 12 }}>{err}</p>}

        {payload && renderSamplingResult()}
      </div>
    </span>
  );
};

const panel = {
  position: 'absolute',
  top: '100%',
  left: 0,
  zIndex: 1000,
  marginTop: 4,
  background: '#f8f9fb',
  border: '1px solid #e2e5ea',
  padding: 12,
  borderRadius: 8,
  minWidth: 400,
  boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
};

const resultBox = {
  background: '#fff',
  border: '1px solid #e6e9ef',
  padding: 12,
  borderRadius: 8
};

export default SamplingButton;